import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import GaConfirmModal from "./GaConfirmModal";
import { GaCancelledError, registerGaPrompt, unregisterGaPrompt } from "../lib/ga-interceptor";

/**
 * Global step-up TOTP provider (docs/305 §4.3, §6.4). Renders a single
 * GaConfirmModal and registers a prompt fn with the interceptor so any 428 from
 * the API pops the modal; the entered code resolves the promise and apiRequest
 * retries with `x-ga-code`. A wrong code yields another 428 → the modal reopens.
 */
export default function GaProvider({ children }: { children: ReactNode }): JSX.Element {
  const resolveRef = useRef<((code: string) => void) | null>(null);
  const rejectRef = useRef<((err: Error) => void) | null>(null);
  const [open, setOpen] = useState(false);

  const promptFn = useCallback((): Promise<string> => {
    // Supersede any in-flight prompt (should not normally happen).
    if (rejectRef.current) rejectRef.current(new GaCancelledError());
    return new Promise<string>((resolve, reject) => {
      resolveRef.current = resolve;
      rejectRef.current = reject;
      setOpen(true);
    });
  }, []);

  useEffect(() => {
    registerGaPrompt(promptFn);
    return () => unregisterGaPrompt();
  }, [promptFn]);

  const handleConfirm = useCallback((code: string) => {
    resolveRef.current?.(code);
    resolveRef.current = null;
    rejectRef.current = null;
    setOpen(false);
  }, []);

  const handleCancel = useCallback(() => {
    rejectRef.current?.(new GaCancelledError());
    resolveRef.current = null;
    rejectRef.current = null;
    setOpen(false);
  }, []);

  return (
    <>
      {children}
      <GaConfirmModal open={open} onConfirm={handleConfirm} onCancel={handleCancel} />
    </>
  );
}
